/**
 * Sistema de cache inteligente para dados da aplicação
 * Resolve problemas de persistência após F5 e melhora performance
 */

interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
  version: string;
}

interface CacheConfig {
  ttl?: number; // Time to live em milissegundos
  version?: string; // Versão dos dados para invalidação
  persistent?: boolean; // Se deve persistir no localStorage
}

class DataCache {
  private memoryCache = new Map<string, CacheItem<any>>();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutos
  private readonly STORAGE_PREFIX = 'saudevirtual_cache_';

  /**
   * Armazenar dados no cache
   */
  set<T>(key: string, data: T, config: CacheConfig = {}): void {
    const {
      ttl = this.DEFAULT_TTL,
      version = '1.0',
      persistent = true
    } = config;

    const now = Date.now();
    const cacheItem: CacheItem<T> = {
      data,
      timestamp: now,
      expiresAt: now + ttl,
      version
    };

    // Armazenar na memória
    this.memoryCache.set(key, cacheItem);

    // Armazenar no localStorage se persistente
    if (persistent) {
      try {
        localStorage.setItem(
          this.STORAGE_PREFIX + key,
          JSON.stringify(cacheItem)
        );
      } catch (error) {
        console.warn('⚠️ Erro ao salvar no localStorage:', error);
      }
    }

    console.log(`💾 Cache armazenado: ${key} (TTL: ${ttl}ms)`);
  }

  /**
   * Recuperar dados do cache
   */
  get<T>(key: string, expectedVersion?: string): T | null {
    let cacheItem: CacheItem<T> | undefined;
    
    try {
      // Primeiro, tentar memória
      cacheItem = this.memoryCache.get(key);

      // Se não encontrou na memória, tentar localStorage
      if (!cacheItem) {
        try {
          const stored = localStorage.getItem(this.STORAGE_PREFIX + key);
          if (stored) {
            cacheItem = JSON.parse(stored);
            // Restaurar na memória
            if (cacheItem) {
              this.memoryCache.set(key, cacheItem);
            }
          }
        } catch (error) {
          console.warn('⚠️ Erro ao ler do localStorage:', error);
          // Limpar entrada corrompida
          try {
            localStorage.removeItem(this.STORAGE_PREFIX + key);
          } catch (cleanupError) {
            console.warn('⚠️ Erro ao limpar entrada corrompida:', cleanupError);
          }
          return null;
        }
      }

      if (!cacheItem) {
        // Não logar como erro se for uma busca normal
        console.log(`📭 Cache miss: ${key}`);
        return null;
      }
    } catch (error) {
      console.warn(`⚠️ Erro geral ao acessar cache ${key}:`, error);
      return null;
    }

    const now = Date.now();

    // Verificar expiração
    if (now > cacheItem.expiresAt) {
      console.log(`⏰ Cache expirado: ${key}`);
      this.delete(key);
      return null;
    }

    // Verificar versão
    if (expectedVersion && cacheItem.version !== expectedVersion) {
      console.log(`🔄 Cache invalidado por versão: ${key}`);
      this.delete(key);
      return null;
    }

    console.log(`✅ Cache hit: ${key}`);
    return cacheItem.data;
  }

  /**
   * Verificar se existe cache válido
   */
  has(key: string, expectedVersion?: string): boolean {
    return this.get(key, expectedVersion) !== null;
  }

  /**
   * Remover item do cache
   */
  delete(key: string): void {
    try {
      this.memoryCache.delete(key);
      try {
        localStorage.removeItem(this.STORAGE_PREFIX + key);
      } catch (error) {
        console.warn('⚠️ Erro ao remover do localStorage:', error);
      }
      console.log(`🗑️ Cache removido: ${key}`);
    } catch (error) {
      console.warn(`⚠️ Erro ao deletar cache ${key}:`, error);
    }
  }

  /**
   * Limpar todo o cache
   */
  clear(): void {
    this.memoryCache.clear();
    
    // Limpar localStorage
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.STORAGE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('⚠️ Erro ao limpar localStorage:', error);
    }
    
    console.log('🧹 Cache limpo completamente');
  }

  /**
   * Invalidar cache por padrão de chave
   */
  invalidatePattern(pattern: string): void {
    const regex = new RegExp(pattern);
    
    // Limpar memória
    for (const key of this.memoryCache.keys()) {
      if (regex.test(key)) {
        this.memoryCache.delete(key);
      }
    }
    
    // Limpar localStorage
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.STORAGE_PREFIX) && regex.test(key.replace(this.STORAGE_PREFIX, ''))) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('⚠️ Erro ao invalidar padrão no localStorage:', error);
    }
    
    console.log(`🔄 Cache invalidado por padrão: ${pattern}`);
  }

  /**
   * Obter estatísticas do cache
   */
  getStats(): { memorySize: number; localStorageSize: number } {
    const memorySize = this.memoryCache.size;
    
    let localStorageSize = 0;
    try {
      const keys = Object.keys(localStorage);
      localStorageSize = keys.filter(key => key.startsWith(this.STORAGE_PREFIX)).length;
    } catch (error) {
      console.warn('⚠️ Erro ao obter estatísticas do localStorage:', error);
    }
    
    return { memorySize, localStorageSize };
  }
}

// Instância singleton do cache
export const dataCache = new DataCache();

// Utilitários para chaves de cache
export const CacheKeys = {
  // Access Control
  PERMISSIONS: (userId: string) => `permissions_${userId}`,
  REQUESTS: (userId: string) => `requests_${userId}`,
  SETTINGS: (userId: string) => `settings_${userId}`,
  
  // Medical Records
  DOCTOR_RECORDS: (doctorId: string) => `doctor_records_${doctorId}`,
  PATIENT_RECORDS: (patientId: string) => `patient_records_${patientId}`,
  
  // Dashboard Stats
  DOCTOR_STATS: (doctorId: string) => `doctor_stats_${doctorId}`,
  PATIENT_STATS: (patientId: string) => `patient_stats_${patientId}`,
  
  // User Profile
  USER_PROFILE: (userId: string) => `user_profile_${userId}`
};

// Configurações de cache por tipo de dados
export const CacheConfigs = {
  // Dados críticos - TTL curto
  CRITICAL: { ttl: 2 * 60 * 1000, persistent: true }, // 2 minutos
  
  // Dados normais - TTL médio
  NORMAL: { ttl: 5 * 60 * 1000, persistent: true }, // 5 minutos
  
  // Dados estáticos - TTL longo
  STATIC: { ttl: 30 * 60 * 1000, persistent: true }, // 30 minutos
  
  // Dados temporários - apenas memória
  TEMPORARY: { ttl: 1 * 60 * 1000, persistent: false } // 1 minuto
};

export default dataCache;